/** A WCAG success-criterion id: dotted non-empty runs of digits, "1.4.10". */
const WELL_FORMED = /^\d+(\.\d+)*$/;

/** Plain code-unit string order: total, and independent of locale. */
const textOrder = (a: string, b: string): number => (a < b ? -1 : a > b ? 1 : 0);

/**
 * Orders WCAG success-criterion ids ("1.4.10") in document order.
 *
 * REV-939: compared as strings, 1.4.10 sorts before 1.4.2. Each dotted segment
 * is an integer, so well-formed ids are compared as numbers, segment by
 * segment, with a shorter prefix first (1.4 before 1.4.1).
 *
 * It must be a total order, or Array.prototype.sort is free to return anything.
 * Falling back to text one SEGMENT at a time is not: 1.2 < 1.10 as numbers,
 * 1.10 < 1.1a and 1.1a < 1.2 as text, which is a cycle. So the fallback is
 * decided per ID instead: every well-formed id sorts before every malformed
 * one, and malformed ids ("", "1..2", "1.a") sort among themselves as plain
 * strings. An empty segment is malformed, never read as 0.
 */
export function compareScIds(a: string, b: string): number {
  const aOk = WELL_FORMED.test(a);
  const bOk = WELL_FORMED.test(b);
  if (aOk !== bOk) return aOk ? -1 : 1;
  if (!aOk) return textOrder(a, b);

  const as = a.split('.').map(Number);
  const bs = b.split('.').map(Number);
  for (let i = 0; i < Math.min(as.length, bs.length); i += 1) {
    const diff = (as[i] as number) - (bs[i] as number);
    if (diff !== 0) return diff;
  }
  if (as.length !== bs.length) return as.length - bs.length;
  // Numerically equal but differently written ("1.04" and "1.4"): break the tie
  // on the text, so distinct ids never compare equal and the order is fixed.
  return textOrder(a, b);
}
